import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Analytics } from '@vercel/analytics/react'
import App from './App.jsx'
import ErrorBoundary, { reloadOnceForChunk } from './components/ErrorBoundary.jsx'
import { installImageFallback } from './utils/imageFallback.js'

installImageFallback()

// A page chunk that fails to download (stale after a deploy, flaky in-app browser) fires this before the
// import rejects. Reload once so shoppers get the fresh build instead of a white screen.
window.addEventListener('vite:preloadError', (e) => { try { e.preventDefault(); } catch {} reloadOnceForChunk(); })

// ─── Render immediately ───────────────────────────────────────────────────────
// Mount the app first so first paint is never blocked by Sentry's ~276KB bundle.
// <Analytics/> = Vercel Web Analytics: counts every page + SPA route change
// (unique visitors, top pages, referrers, devices) entirely client-side — no
// backend/DB load. Must also be enabled in the Vercel dashboard (Project → Analytics).
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
        <Analytics />
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>,
)

// ─── SENTRY (deferred, off the critical path) ─────────────────────────────────
// Loaded via dynamic import AFTER the app is interactive — Vite splits @sentry/react
// into its own chunk that fetches lazily, so the storefront paints fast. Error
// tracking is unchanged apart from a ~1–2s warm-up window on first load.
if (import.meta.env.VITE_SENTRY_DSN) {
  const initSentry = () => {
    import("@sentry/react").then((Sentry) => {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.MODE || "production",
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
        ],
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        enabled: true,
        // "Load failed" / "Failed to fetch" are Safari's TypeError when a fetch is
        // aborted by navigation (user taps Back mid-load). Not a real error.
        ignoreErrors: ["Load failed", "Failed to fetch", /NetworkError/],
        beforeSend(event) {
          const isNetworkAbort =
            event.exception?.values?.every(ex =>
              (ex.type === "TypeError" || ex.type === "Error") &&
              (!ex.stacktrace?.frames?.length) &&
              ["Load failed", "Failed to fetch", "NetworkError"].some(m => ex.value?.includes(m))
            );
          if (isNetworkAbort) return null;
          return event;
        },
      });
    }).catch(() => { /* Sentry is non-critical — never block the app on it */ });
  };
  if ("requestIdleCallback" in window) window.requestIdleCallback(initSentry, { timeout: 3000 });
  else setTimeout(initSentry, 2000);
}
